import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ChatTopic, newId, QuickReply, topicLabel } from './liveChatTypes';

interface Props {
  replies: QuickReply[];
  topics: ChatTopic[];
  onChange: (replies: QuickReply[]) => void;
  onLoadSample: () => void;
  loadingSample?: boolean;
}

interface TreeRow {
  reply: QuickReply;
  depth: number;
}

function buildTree(replies: QuickReply[]): TreeRow[] {
  const ids = new Set(replies.map((r) => r.id));
  const byParent = new Map<string, QuickReply[]>();
  replies.forEach((r) => {
    const key = r.parentId && ids.has(r.parentId) ? r.parentId : '';
    byParent.set(key, [...(byParent.get(key) || []), r]);
  });
  const rows: TreeRow[] = [];
  const seen = new Set<string>();
  const walk = (parent: string, depth: number) => {
    (byParent.get(parent) || []).forEach((r) => {
      if (seen.has(r.id)) return;
      seen.add(r.id);
      rows.push({ reply: r, depth });
      walk(r.id, depth + 1);
    });
  };
  walk('', 0);
  // Döngüde kalanlar (kaydedilince ana menüye alınır)
  replies.forEach((r) => !seen.has(r.id) && rows.push({ reply: r, depth: 0 }));
  return rows;
}

function descendantsOf(replies: QuickReply[], id: string) {
  const result = new Set<string>();
  const stack = [id];
  while (stack.length) {
    const current = stack.pop()!;
    replies.forEach((r) => {
      if (r.parentId === current && !result.has(r.id)) {
        result.add(r.id);
        stack.push(r.id);
      }
    });
  }
  return result;
}

function pathOf(replies: QuickReply[], id: string) {
  const byId = new Map(replies.map((r) => [r.id, r]));
  const path: QuickReply[] = [];
  let cursor = byId.get(id);
  const guard = new Set<string>();
  while (cursor && !guard.has(cursor.id)) {
    guard.add(cursor.id);
    path.unshift(cursor);
    cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined;
  }
  return path;
}

const LiveChatFlowEditor: React.FC<Props> = ({ replies, topics, onChange, onLoadSample, loadingSample }) => {
  const rows = useMemo(() => buildTree(replies), [replies]);
  const [selectedId, setSelectedId] = useState<string | null>(rows[0]?.reply.id || null);
  const selected = replies.find((r) => r.id === selectedId) || null;

  useEffect(() => {
    if (!selected && rows.length) setSelectedId(rows[0].reply.id);
  }, [selected, rows]);

  const maxDepth = rows.reduce((max, row) => Math.max(max, row.depth + 1), 0);
  const childCount = (id: string) => replies.filter((r) => r.parentId === id).length;
  const children = selected ? replies.filter((r) => r.parentId === selected.id) : [];
  const path = selected ? pathOf(replies, selected.id) : [];
  const blocked = selected ? descendantsOf(replies, selected.id) : new Set<string>();

  const update = (id: string, patch: Partial<QuickReply>) => {
    onChange(replies.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const add = (parentId: string | null) => {
    const item: QuickReply = { id: newId(), label: '', answer: '', keywords: [], handover: false, parentId, topic: null };
    // Alt soruyu üstünün son çocuğundan sonra ekle; liste sırası ziyaretçideki sıradır.
    const next = [...replies];
    if (parentId) {
      const family = new Set([parentId, ...Array.from(descendantsOf(replies, parentId))]);
      let insertAt = -1;
      next.forEach((r, i) => { if (family.has(r.id)) insertAt = i; });
      next.splice(insertAt + 1, 0, item);
    } else {
      next.push(item);
    }
    onChange(next);
    setSelectedId(item.id);
  };

  const remove = (id: string) => {
    const doomed = descendantsOf(replies, id);
    if (doomed.size > 0 && !window.confirm(`Bu soru ve altındaki ${doomed.size} soru silinecek. Devam edilsin mi?`)) return;
    doomed.add(id);
    const parent = replies.find((r) => r.id === id)?.parentId || null;
    onChange(replies.filter((r) => !doomed.has(r.id)));
    setSelectedId(parent);
  };

  const keywordText = (selected?.keywords || []).join(', ');

  return (
    <Paper variant="outlined" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '340px 1fr' }, minHeight: 520, overflow: 'hidden' }}>
      {/* Ağaç */}
      <Box sx={{ borderRight: { md: '1px solid' }, borderColor: { md: 'divider' }, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>Sorular</Typography>
            <Typography variant="caption" color="text.secondary">
              {replies.length} soru · en derin dal {maxDepth} adım
            </Typography>
          </Box>
          <Button size="small" startIcon={<AddIcon />} onClick={() => add(null)}>Ana soru</Button>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5, maxHeight: { md: 'calc(100vh - 340px)' } }}>
          {rows.map(({ reply, depth }) => {
            const active = reply.id === selectedId;
            const kids = childCount(reply.id);
            return (
              <Box
                key={reply.id}
                onClick={() => setSelectedId(reply.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  pl: 1.5 + depth * 2,
                  pr: 1.5,
                  py: 0.75,
                  cursor: 'pointer',
                  borderLeft: '3px solid',
                  borderLeftColor: active ? 'primary.main' : 'transparent',
                  bgcolor: active ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: active ? 'action.selected' : 'action.hover' },
                }}
              >
                {depth > 0 && <SubdirectoryArrowRightIcon sx={{ fontSize: 15, color: 'text.disabled' }} />}
                <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: depth === 0 ? 600 : 400, color: reply.label ? 'text.primary' : 'text.disabled' }}>
                  {reply.label || 'Adsız soru'}
                </Typography>
                {reply.handover && (
                  <Tooltip title={`Temsilciye aktarır${reply.topic ? ` · ${topicLabel(topics, reply.topic)}` : ''}`}>
                    <SupportAgentIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                  </Tooltip>
                )}
                {kids > 0 && (
                  <Typography variant="caption" color="text.secondary">{kids}</Typography>
                )}
              </Box>
            );
          })}
          {replies.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <AccountTreeOutlinedIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
                Henüz soru yok. Hazır akışla başlayıp düzenleyebilirsiniz.
              </Typography>
              <Button size="small" variant="outlined" onClick={onLoadSample} disabled={loadingSample}>Örnek akışı yükle</Button>
            </Box>
          )}
        </Box>
        {replies.length > 0 && (
          <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button size="small" color="inherit" onClick={onLoadSample} disabled={loadingSample} sx={{ color: 'text.secondary' }}>
              Örnek akışla değiştir
            </Button>
          </Box>
        )}
      </Box>

      {/* Düzenleyici */}
      <Box sx={{ p: { xs: 2, md: 3 }, overflowY: 'auto', maxHeight: { md: 'calc(100vh - 290px)' } }}>
        {maxDepth > 0 && maxDepth < 3 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Ziyaretçiyi doğru ekibe yönlendirmek için her ana soru altında en az 3 adımlık dal kurun (ör. Sipariş › Siparişim nerede › Teslim süresi geçti › temsilci).
          </Alert>
        )}
        {!selected ? (
          <Typography color="text.secondary">Soldan bir soru seçin veya yeni ana soru ekleyin.</Typography>
        ) : (
          <Stack spacing={2.5}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Ana menü</Typography>
              {path.map((p) => (
                <React.Fragment key={p.id}>
                  <ChevronRightIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography
                    variant="caption"
                    onClick={() => setSelectedId(p.id)}
                    sx={{ cursor: 'pointer', fontWeight: p.id === selected.id ? 700 : 400, color: p.id === selected.id ? 'text.primary' : 'text.secondary', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {p.label || 'Adsız soru'}
                  </Typography>
                </React.Fragment>
              ))}
              <Box sx={{ flex: 1 }} />
              <Tooltip title={blocked.size ? 'Soruyu ve alt sorularını sil' : 'Soruyu sil'}>
                <IconButton size="small" onClick={() => remove(selected.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Stack>

            <TextField
              label="Ziyaretçinin seçtiği soru"
              size="small"
              fullWidth
              value={selected.label}
              onChange={(e) => update(selected.id, { label: e.target.value })}
              placeholder="ör. Siparişim nerede?"
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              label="Bot cevabı"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={selected.answer}
              onChange={(e) => update(selected.id, { answer: e.target.value })}
              helperText="Sayfa bağlantısı için /contact veya /pages/sayfa-adi yazabilirsiniz."
            />
            <TextField
              label="Anahtar kelimeler"
              size="small"
              fullWidth
              value={keywordText}
              placeholder="kargo, gecikti, nerede"
              helperText="Ziyaretçi yazarak sorarsa bu kelimelerle eşleşir. Virgülle ayırın."
              onChange={(e) => {
                const raw = e.target.value;
                const parts = raw.split(',').map((k) => k.trim());
                const keywords = raw.endsWith(',') ? [...parts.filter(Boolean), ''] : parts.filter(Boolean);
                update(selected.id, { keywords });
              }}
            />

            <FormControl size="small" fullWidth>
              <InputLabel>Bağlı olduğu soru</InputLabel>
              <Select
                label="Bağlı olduğu soru"
                value={selected.parentId || ''}
                onChange={(e) => update(selected.id, { parentId: (e.target.value as string) || null })}
              >
                <MenuItem value=""><em>Ana menü</em></MenuItem>
                {rows
                  .filter(({ reply }) => reply.id !== selected.id && !blocked.has(reply.id))
                  .map(({ reply, depth }) => (
                    <MenuItem key={reply.id} value={reply.id} sx={{ pl: 2 + depth * 2 }}>
                      {reply.label || 'Adsız soru'}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={700}>Cevaptan sonra</Typography>
              <Typography variant="caption" color="text.secondary">
                Ziyaretçiye alt sorular gösterilir ya da görüşme temsilciye aktarılır.
              </Typography>
            </Box>

            <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, borderColor: selected.handover ? 'warning.light' : 'divider' }}>
              <SupportAgentIcon sx={{ color: selected.handover ? 'warning.main' : 'text.disabled' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600}>Temsilciye aktar</Typography>
                <Typography variant="caption" color="text.secondary">Cevaptan sonra görüşme Görüşmeler sekmesine düşer.</Typography>
              </Box>
              {selected.handover && (
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel>Konu</InputLabel>
                  <Select
                    label="Konu"
                    value={selected.topic || ''}
                    onChange={(e) => update(selected.id, { topic: (e.target.value as string) || null })}
                  >
                    <MenuItem value=""><em>Ziyaretçi seçsin</em></MenuItem>
                    {topics.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              <Switch checked={!!selected.handover} onChange={(e) => update(selected.id, { handover: e.target.checked, topic: e.target.checked ? selected.topic : null })} />
            </Paper>

            {selected.handover && children.length > 0 && (
              <Alert severity="warning">Temsilciye aktaran soruda alt sorular gösterilmez. Alt soruları başka bir soruya taşıyın veya aktarımı kapatın.</Alert>
            )}

            {!selected.handover && (
              <Box>
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                  {children.map((c) => (
                    <Chip key={c.id} label={c.label || 'Adsız soru'} onClick={() => setSelectedId(c.id)} variant="outlined" size="small" />
                  ))}
                  <Chip icon={<AddIcon />} label="Alt soru ekle" onClick={() => add(selected.id)} color="primary" variant="outlined" size="small" />
                </Stack>
                {children.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Alt soru yoksa ziyaretçi "Ana menü" ve "Temsilciye bağlan" seçeneklerini görür.
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

export default LiveChatFlowEditor;
